// Service for array-based handicapper picks storage
// Document per handicapper (doc id = handicapper UID) with a `picks` array and precomputed stats.

import { db } from './firebase'; // assumes existing firebase client export
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
  increment,
  runTransaction,
} from 'firebase/firestore';

// Collection name for the new structure
const COLLECTION = 'handicapperPicksV2';

// Helper to generate a compact unique pick id
function generatePickId() {
  return `pick_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Create a pick for a handicapper. Uses `arrayUnion` to append the pick object
 * and atomic increments for stats.
 *
 * pickData must include at minimum: handicapperId, handicapperName, handicapperSlug,
 * gameId, sport, homeTeam, awayTeam, pickType, pickedTeam, odds, confidence, bookmaker, commenceTime
 */
export async function createPick(pickData) {
  if (!pickData || !pickData.handicapperId) {
    throw new Error('pickData.handicapperId is required');
  }

  const pickId = generatePickId();
  // Use numeric timestamps for array elements because sentinel values
  // (serverTimestamp) are not allowed inside arrayUnion elements.
  const nowNumeric = Date.now();

  const pick = {
    id: pickId,
    gameId: pickData.gameId || null,
    sport: pickData.sport || null,
    homeTeam: pickData.homeTeam || null,
    awayTeam: pickData.awayTeam || null,
    pickType: pickData.pickType || null,
    pickedTeam: pickData.pickedTeam || null,
    odds: typeof pickData.odds === 'number' ? pickData.odds : null,
    confidence: pickData.confidence || 'medium',
    analysis: pickData.analysis || 'No analysis provided',
    bookmaker: pickData.bookmaker || null,
    commenceTime: pickData.commenceTime || null,
    result: 'pending',
    createdAt: nowNumeric,
    updatedAt: nowNumeric,
  };

  const docRef = doc(db, COLLECTION, pickData.handicapperId);

  try {
    // Create the document if it doesn't exist, merging base fields
    // We attempt an update first to avoid overwriting; setDoc with merge will create if missing
    // Set base document (merge) using server timestamp for server-side ordering
    await setDoc(docRef, {
      handicapperId: pickData.handicapperId,
      handicapperName: pickData.handicapperName || null,
      handicapperSlug: pickData.handicapperSlug || null,
      lastPickDate: serverTimestamp(),
    }, { merge: true });

    // Append pick and increment totalPicks
    await updateDoc(docRef, {
      picks: arrayUnion(pick),
      totalPicks: increment(1),
      // lastPickDate kept as serverTimestamp for accurate server time
      lastPickDate: serverTimestamp(),
    });

    return pickId;
  } catch (err) {
    console.error('createPick error', err);
    throw err;
  }
}

/**
 * Transactional create - ensures atomicity when adding picks and updating stats.
 * Returns the created pick object (including generated id and timestamps).
 */
export async function createPickV2(pickData) {
  if (!pickData || !pickData.handicapperId) throw new Error('pickData.handicapperId is required');

  const pickId = generatePickId();
  const nowNumeric = Date.now();
  const pick = {
    id: pickId,
    gameId: pickData.gameId || null,
    sport: pickData.sport || null,
    homeTeam: pickData.homeTeam || null,
    awayTeam: pickData.awayTeam || null,
    pickType: pickData.pickType || null,
    pickedTeam: pickData.pickedTeam || null,
    odds: typeof pickData.odds === 'number' ? pickData.odds : null,
    confidence: pickData.confidence || 'medium',
    analysis: pickData.analysis || 'No analysis provided',
    bookmaker: pickData.bookmaker || null,
    commenceTime: pickData.commenceTime || null,
    result: 'pending',
    aiGenerated: pickData.aiGenerated || false, // Add AI-generated flag
    createdAt: nowNumeric,
    updatedAt: nowNumeric,
  };

  const docRef = doc(db, COLLECTION, pickData.handicapperId);

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      if (!snap.exists()) {
        tx.set(docRef, {
          handicapperId: pickData.handicapperId,
          handicapperName: pickData.handicapperName || null,
          handicapperSlug: pickData.handicapperSlug || null,
          picks: [pick],
          totalPicks: 1,
          wins: 0,
          losses: 0,
          pushes: 0,
          lastPickDate: serverTimestamp(),
        });
      } else {
        // merge new pick into existing array and bump counters
        tx.update(docRef, {
          picks: arrayUnion(pick),
          totalPicks: increment(1),
          lastPickDate: serverTimestamp(),
        });
      }
    });

    return pick;
  } catch (err) {
    console.error('createPickV2 error', err);
    throw err;
  }
}

/**
 * Retrieve picks document for a handicapper (full doc)
 */
export async function getPicksDocument(handicapperId) {
  if (!handicapperId) throw new Error('handicapperId required');
  const docRef = doc(db, COLLECTION, handicapperId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return snap.data();
}

/**
 * Get picks normalized for UI use. Converts numeric timestamps to JS Date objects.
 */
export async function getPicksForHandicapperV2(handicapperId) {
  const data = await getPicksDocument(handicapperId);
  if (!data) return { picks: [], totalPicks: 0, wins: 0, losses: 0, pushes: 0 };

  const picks = Array.isArray(data.picks)
    ? data.picks.map((p) => ({
      ...p,
      createdAt: p.createdAt ? new Date(p.createdAt) : null,
      updatedAt: p.updatedAt ? new Date(p.updatedAt) : null,
    }))
    : [];

  return {
    picks,
    totalPicks: data.totalPicks || picks.length,
    wins: data.wins || 0,
    losses: data.losses || 0,
    pushes: data.pushes || 0,
    lastPickDate: data.lastPickDate || null,
  };
}

/**
 * Update a specific pick's result (won/lost/push/pending).
 * Because Firestore doesn't support updating an array element by index natively,
 * we fetch the document, replace the pick in-memory, then write the updated picks array.
 * We also update pre-computed stats (wins/losses/pushes).
 */
export async function updatePickResult(handicapperId, pickId, newResult) {
  if (!handicapperId || !pickId) throw new Error('handicapperId and pickId required');

  const docRef = doc(db, COLLECTION, handicapperId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error('Handicapper document not found');

  const data = snap.data();
  const picks = Array.isArray(data.picks) ? data.picks : [];

  let found = false;
  let statDelta = { wins: 0, losses: 0, pushes: 0 };

  const updatedPicks = picks.map((p) => {
    if (p.id === pickId) {
      found = true;
      const old = p.result || 'pending';
      const updated = { ...p, result: newResult, updatedAt: serverTimestamp() };

      // adjust deltas
      if (old === 'won') statDelta.wins -= 1;
      if (old === 'lost') statDelta.losses -= 1;
      if (old === 'push') statDelta.pushes -= 1;

      if (newResult === 'won') statDelta.wins += 1;
      if (newResult === 'lost') statDelta.losses += 1;
      if (newResult === 'push') statDelta.pushes += 1;

      return updated;
    }
    return p;
  });

  if (!found) throw new Error('Pick not found');

  // Prepare update object
  const updates = {
    picks: updatedPicks,
    lastPickDate: serverTimestamp(),
  };

  if (statDelta.wins !== 0) updates.wins = increment(statDelta.wins);
  if (statDelta.losses !== 0) updates.losses = increment(statDelta.losses);
  if (statDelta.pushes !== 0) updates.pushes = increment(statDelta.pushes);

  try {
    await updateDoc(docRef, updates);
    return true;
  } catch (err) {
    console.error('updatePickResult error', err);
    throw err;
  }
}

/**
 * General update for a pick's editable fields. Uses transaction to atomically modify the picks array.
 * `updates` may include analysis, odds, confidence, commenceTime, pickedTeam, pickType, result, etc.
 */
export async function updatePickV2(handicapperId, pickId, updates) {
  if (!handicapperId || !pickId) throw new Error('handicapperId and pickId required');

  const docRef = doc(db, COLLECTION, handicapperId);

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      if (!snap.exists()) throw new Error('Handicapper document not found');

      const data = snap.data();
      const picks = Array.isArray(data.picks) ? data.picks.slice() : [];

      let found = false;
      let statDelta = { wins: 0, losses: 0, pushes: 0 };

      const newPicks = picks.map((p) => {
        if (p.id === pickId) {
          found = true;
          const oldResult = p.result || 'pending';
          const merged = { ...p, ...updates, updatedAt: Date.now() };

          const newResult = merged.result || oldResult;
          if (oldResult === 'won') statDelta.wins -= 1;
          if (oldResult === 'lost') statDelta.losses -= 1;
          if (oldResult === 'push') statDelta.pushes -= 1;

          if (newResult === 'won') statDelta.wins += 1;
          if (newResult === 'lost') statDelta.losses += 1;
          if (newResult === 'push') statDelta.pushes += 1;

          return merged;
        }
        return p;
      });

      if (!found) throw new Error('Pick not found');

      const txUpdates = { picks: newPicks, lastPickDate: serverTimestamp() };
      if (statDelta.wins !== 0) txUpdates.wins = increment(statDelta.wins);
      if (statDelta.losses !== 0) txUpdates.losses = increment(statDelta.losses);
      if (statDelta.pushes !== 0) txUpdates.pushes = increment(statDelta.pushes);

      tx.update(docRef, txUpdates);
    });

    return true;
  } catch (err) {
    console.error('updatePickV2 error', err);
    throw err;
  }
}

/**
 * Delete a pick from the array. This reads the document, filters out the pick
 * and writes the new array. It also decrements totalPicks and adjusts stats.
 */
export async function deletePick(handicapperId, pickId) {
  if (!handicapperId || !pickId) throw new Error('handicapperId and pickId required');

  const docRef = doc(db, COLLECTION, handicapperId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) throw new Error('Handicapper document not found');

  const data = snap.data();
  const picks = Array.isArray(data.picks) ? data.picks : [];

  const pickToDelete = picks.find((p) => p.id === pickId);
  if (!pickToDelete) throw new Error('Pick not found');

  const updatedPicks = picks.filter((p) => p.id !== pickId);

  const updates = {
    picks: updatedPicks,
    totalPicks: increment(-1),
    lastPickDate: serverTimestamp(),
  };

  // adjust stats if needed
  if (pickToDelete.result === 'won') updates.wins = increment(-1);
  if (pickToDelete.result === 'lost') updates.losses = increment(-1);
  if (pickToDelete.result === 'push') updates.pushes = increment(-1);

  try {
    await updateDoc(docRef, updates);
    return true;
  } catch (err) {
    console.error('deletePick error', err);
    throw err;
  }
}

/**
 * Transactional delete of a pick inside the handicapper doc.
 */
export async function deletePickV2(handicapperId, pickId) {
  if (!handicapperId || !pickId) throw new Error('handicapperId and pickId required');

  const docRef = doc(db, COLLECTION, handicapperId);

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(docRef);
      if (!snap.exists()) throw new Error('Handicapper document not found');

      const data = snap.data();
      const picks = Array.isArray(data.picks) ? data.picks.slice() : [];

      const pickToDelete = picks.find((p) => p.id === pickId);
      if (!pickToDelete) throw new Error('Pick not found');

      const updatedPicks = picks.filter((p) => p.id !== pickId);

      const txUpdates = {
        picks: updatedPicks,
        totalPicks: increment(-1),
        lastPickDate: serverTimestamp(),
      };

      if (pickToDelete.result === 'won') txUpdates.wins = increment(-1);
      if (pickToDelete.result === 'lost') txUpdates.losses = increment(-1);
      if (pickToDelete.result === 'push') txUpdates.pushes = increment(-1);

      tx.update(docRef, txUpdates);
    });

    return true;
  } catch (err) {
    console.error('deletePickV2 error', err);
    throw err;
  }
}

/**
 * Clean up expired picks for a handicapper (picks for games that have already started).
 * This is useful for BettorAI to remove old picks after games are over.
 * Returns the number of picks removed and their details.
 */
export async function cleanupExpiredPicks(handicapperId) {
  if (!handicapperId) throw new Error('handicapperId required');

  const docRef = doc(db, COLLECTION, handicapperId);
  const snap = await getDoc(docRef);

  if (!snap.exists()) {
    console.log('Handicapper document not found, nothing to cleanup');
    return { removed: 0, remaining: 0 };
  }

  const data = snap.data();
  const picks = Array.isArray(data.picks) ? data.picks : [];
  const now = new Date();

  // Separate valid (future) and expired (past) picks
  const validPicks = [];
  const expiredPicks = [];

  for (const pick of picks) {
    if (pick.commenceTime) {
      const gameTime = new Date(pick.commenceTime);
      if (gameTime <= now) {
        expiredPicks.push(pick);
      } else {
        validPicks.push(pick);
      }
    } else {
      // Keep picks without commenceTime for safety
      validPicks.push(pick);
    }
  }

  if (expiredPicks.length === 0) {
    return { removed: 0, remaining: validPicks.length };
  }

  // Calculate stat adjustments for removed picks
  let winsToRemove = 0;
  let lossesToRemove = 0;
  let pushesToRemove = 0;

  for (const pick of expiredPicks) {
    if (pick.result === 'won') winsToRemove++;
    if (pick.result === 'lost') lossesToRemove++;
    if (pick.result === 'push') pushesToRemove++;
  }

  // Update the document with filtered picks
  const updates = {
    picks: validPicks,
    totalPicks: increment(-expiredPicks.length),
    lastPickDate: serverTimestamp(),
  };

  if (winsToRemove > 0) updates.wins = increment(-winsToRemove);
  if (lossesToRemove > 0) updates.losses = increment(-lossesToRemove);
  if (pushesToRemove > 0) updates.pushes = increment(-pushesToRemove);

  try {
    await updateDoc(docRef, updates);
    return {
      removed: expiredPicks.length,
      remaining: validPicks.length,
      expiredPicks: expiredPicks.map(p => ({
        id: p.id,
        gameId: p.gameId,
        homeTeam: p.homeTeam,
        awayTeam: p.awayTeam,
        sport: p.sport,
        commenceTime: p.commenceTime,
        result: p.result
      }))
    };
  } catch (err) {
    console.error('cleanupExpiredPicks error', err);
    throw err;
  }
}

export default {
  createPick,
  createPickV2,
  getPicksDocument,
  getPicksForHandicapperV2,
  updatePickResult,
  updatePickV2,
  deletePick,
  deletePickV2,
  cleanupExpiredPicks,
};
